"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useNavigate } from 'react-router-dom';
import { Loader, Plus, Trash2, UploadCloud } from "lucide-react";
import useSWRMutation from "swr/mutation"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Textarea } from "../../../components/ui/textarea"
import { Label } from "../../../components/ui/label"
import uploadToS3 from "../../../util/uploadToS3";
import axiosConfig from "../../../util/axiosConfig";
import requestErrorHandler from "../../../util/requestErrorHandler";
import BreadCrumb from "../../../componentsV2/BreadCrumb";
import { Radio } from "antd";


// Define the API fetcher function for SWR
async function createTruckCategory(url: string, { arg }: { arg: TruckCategoryData }) {

  try {
    const response = await axiosConfig.post(url, arg);
    return response.data;
  } catch (error: any) {
    console.error("API Error:", error?.response?.data || error.message);
    throw error
  }
  
}

// Define types
interface Material {
  image?: string
  name: string
}

interface TruckCategoryData {
  name: string
  code: string
  gdCharge: string
  gdChargeType: string
  description: string
  image?: File | string | null
  materials: Material[]
}

export default function CreateTruckCategory() {
  //   const router = useRouter()
  const navigate = useNavigate();
  const [formData, setFormData] = useState<TruckCategoryData>({
    name: "",
    code: "",
    description: "",
    image: null,
    gdCharge: "",
    gdChargeType: "flat",
    materials: [{
      name: '',
      image: ''
    }]
  })
  const [materials, setMaterials] = useState<TruckCategoryData["materials"]>([{
    name: "", image: ""
  }])
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [isFormValid, setIsFormValid] = useState(false)
  const [uploading, setUploading] = useState<Record<string, boolean>>({})
  const [categoryFile, setCategoryFile] = useState<File | null>(null)
  const [categoryUploading, setCategoryUploading] = useState(false)
  const [materialFiles, setMaterialFiles] = useState<Record<string, File | null>>({})

  // SWR mutation hook
  const { trigger, isMutating } = useSWRMutation(
    "/newAdmin/create-truck-category",
    createTruckCategory,
  )



  const handleMaterialFileUpload = async (name: string, file: File, idx: number) => {
    if (!file) return

    setUploading((prev) => ({ ...prev, [name]: true }))
    try {
      await uploadToS3({
        file,
        data: {},
        filename: file.name,
        onSuccess: (url: string) => {
          setFormData((prev) => ({
            ...prev,
            materials: prev.materials.map((material, index) =>
              index === idx ? { ...material, image: url } : material,
            ),
          }))
        },
        onError: (error: any) => {
          console.error("Upload failed:", error)
        },
      })
    } catch (error) {
      console.error("Upload error:", error)
    } finally {
      setUploading((prev) => ({ ...prev, [name]: false }))
    }
  }

  
  const handleCategoryImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    if (!file) return

    setCategoryFile(file)

    // Show preview immediately
    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result as string)
    }
    reader.readAsDataURL(file)

    function removeTimestamp(url:any) {
      // return url.replace(/\/\d{4,}\.\d{3}Z$/, '');
      return url
    }

    // Upload to S3
    setCategoryUploading(true)
    try {
      await uploadToS3({
        file,
        data: {},
        filename: file.name,
        onSuccess: (url: string) => {
          setFormData((prev) => ({ ...prev, image: removeTimestamp(url!) }))
        },
        onError: (error: any) => {
          console.error("Upload failed:", error.error)
          requestErrorHandler(error)
        },
      })
    } catch (error) {
      console.error("Upload error:", error)
    } finally {
      setCategoryUploading(false)
    }
  }

  // Validate form
  useEffect(() => {
    const { name, code, description, materials } = formData;
    const allMaterialsValid = materials.every((m) => m.name.trim() !== "" && m.image && m.image.trim() !== "");
    const isValid = name.trim() !== "" && code.trim() !== "" && description.trim() !== "" && allMaterialsValid;

    setIsFormValid(isValid);
  }, [formData])

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  // Handle material input changes
  const handleMaterialChange = (value: string, idx: number) => {
    // const _mat = [...materials!];
    // _mat[idx].name = value;
    // setMaterials(_mat);

    setFormData((prev) =>{ 
      let newList = [...prev.materials]
      newList[idx] = { ...newList[idx], name: value }
      return { ...prev, materials: newList }
     });
  };

  // Add new material field
  const addMaterial = () => {
    setFormData((prev) => ({
      ...prev,
      materials: [...prev.materials, { name: "" }],
    }))

    // setMaterials(prev => [...prev!, {name:"",image:""}])
  }

  // Remove material field
  const removeMaterial = (name: string, index:number) => {
    if (formData.materials.length <=1){
      return
    }
    setFormData((prev) => ({
      ...prev,
      materials: prev.materials.filter((material, idx) => idx !==index),
    }))
  }

  // Handle image upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setFormData((prev) => ({ ...prev, image: file }))

    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    } else {
      setImagePreview(null)
    }
  }

  // Handle form submission
  const handleSubmit = async () => {
    if (!isFormValid) return;

    try {
      // Convert the form data to the format expected by the API
      const apiData = {
        ...formData,
        materials: formData.materials.map((m) => ({ image: m.image, name: m.name.toLowerCase() })),
      }

      await trigger(apiData)
      // On success, navigate back or show success message
      navigate(-1)
    } catch (error:any) {
      requestErrorHandler(error)
      console.error("Error creating truck category:", error)
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <div >
        {/* Main content */}
        <main className="flex-1 p-8">
          <div className="max-w-5xl mx-auto bg-white p-8 rounded-lg shadow-sm border">
            {/* Back button and title */}
           <BreadCrumb label="Create Category" showBack />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Left column */}
              <div className="space-y-6">
                {/* Category Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">Category Name</Label>
                  <div className="relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-600 "></div>
                    <Input
                      id="name"
                      name="name"
                      placeholder="Vehicle Category"
                      className="pl-3 bg-gray-100"
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <div className="relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-600 "></div>
                    <Textarea
                      id="description"
                      name="description"
                      placeholder="Enter category description"
                      className="pl-3 bg-gray-100 min-h-[150px]"
                      value={formData.description}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Materials */}
                <div className="space-y-2">
                  <Label>Materials</Label>
                  {formData.materials?.map((material, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <div className="relative flex-1">
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-600"></div>
                        <Input
                          placeholder="Material Name"
                          className="pl-3 bg-gray-100"
                          value={material.name}
                          onChange={(e) => {
                            const value = e.target.value; // Store value outside of event callback
                            handleMaterialChange(value, index)
                       
                          }}
                        />
                      </div>

                      {/* Material actions */}
                      <div className="flex gap-1">
                        <div className="relative">
                          <input
                            type="file"
                            id={`material-image-${material.name}`}
                            className="hidden"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0]
                              if (file) {
                                setMaterialFiles((prev) => ({ ...prev, [material.name]: file }))
                                handleMaterialFileUpload(material.name, file, index)
                              }
                            }}
                          />
                          <label
                            htmlFor={`material-image-${material.name}`}
                            className={`flex items-center justify-center h-10 w-10 rounded border cursor-pointer ${material.image ? "bg-blue-100 border-blue-300" : "bg-blue-50 border-blue-100"
                              }`}
                          >
                            {material.image ? (
                                <img
                                src={material.image || "/placeholder.svg"}
                                alt="Category preview"
                                className="w-full h-full object-contain"
                              />
                            ) : (<span className="flex items-center justify-center">
                              {uploading[material.name]
                                ? (<Loader size={20} className="animate-spin text-primary" />)
                                : <UploadCloud size={20} className="text-primary"  />}
                              </span>)}
                          </label>
                        </div>

                        <Button
                          variant="outline"
                          size="icon"
                          className="h-10 w-10 bg-red-50 border-red-100"
                          onClick={() => removeMaterial(material.name, index)}
                        >
                          <Trash2 className="h-5 w-5 text-red-500" />
                        </Button>

                        {index === formData.materials.length - 1 && (
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-10 w-10 bg-blue-50 border-blue-100"
                            onClick={addMaterial}
                          >
                            <Plus className="h-5 w-5 text-blue-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right column */}
              <div className="space-y-6">
                {/* Category Code */}
                <div className="space-y-2">
                  <Label htmlFor="code">Category Code</Label>
                  <div className="relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-600 "></div>
                    <Input
                      id="code"
                      name="code"
                      placeholder="DT30T"
                      className="pl-3 bg-gray-100"
                      value={formData.code}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gdChargeType">Category Charge Type</Label>
                  <Radio.Group
                    id="gdChargeType"
                    name="gdChargeType"
                    value={formData.gdChargeType}
                    onChange={(e) => setFormData({...formData, gdChargeType: e.target.value})}
                    optionType="button"
                    buttonStyle="solid"
                    className="w-full"
                  >
                    <Radio value="flat">Flat</Radio>
                    <Radio value="percentage">Percentage</Radio>
                  </Radio.Group>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gdCharge">Category Charge</Label>
                  <div className="relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-600 "></div>
                    <Input
                      id="gdCharge"
                      name="gdCharge"
                      placeholder="1000"
                      className="pl-3 bg-gray-100"
                      value={formData.gdCharge}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Category Image */}
                <div className="space-y-2">
                  <Label>Category Image</Label>
                  <div className="border border-dashed border-blue-300 max-h-[150px]  p-4 bg-blue-50">
                    <div className="flex flex-col items-center justify-center">
                      {imagePreview ? (
                        <div className="relative w-full h-32">
                          <img
                            src={imagePreview || "/placeholder.svg"}
                            alt="Category preview"
                            className="w-full h-full object-contain"
                          />
                          <Button
                            variant="destructive"
                            size="sm"
                            className="absolute top-2 right-2"
                            onClick={() => {
                              setImagePreview(null)
                              setFormData((prev) => ({ ...prev, image: null }))
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <label className="w-full cursor-pointer">
                          <div className="flex flex-col items-center justify-center py-6">
                            <UploadCloud size={50} className="text-primary" />
                            <p className="text-sm text-blue-500">Upload Image</p>
                            {categoryUploading && (
                              <div className="mt-2 h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            )}
                          </div>
                          <input type="file" className="hidden" accept="image/*" onChange={handleCategoryImageUpload} />
                        </label>
                      )}
                    </div>
                  </div>
                </div>

                {/* Create Button */}
                <div className="pt-12">
                  <Button
                    className="w-full h-12 mt-auto text-white"
                    disabled={!isFormValid || isMutating}
                    onClick={handleSubmit}
                    // variant={isFormValid ? "default" : "secondary"}
                  >
                    {isMutating ? "Creating..." : "Create"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

